import { Injectable } from '@angular/core';
import {
  ADMIN,
  APPLICATION_NAMES,
  CACHED_COMPANY,
  CACHED_LOCATION,
  CLIENT_ID_QUERY_PARAM,
  EPM_EFAN,
  INTEGRATION_ERROR,
  PECONN_TOKEN,
  PeConnAccessToken,
  REDIRECT_FEATURE,
  SERVER_ERROR,
} from '@share/constants';
import { Observable, of, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InterceptorTypeEnum } from './InterceptorType.enum';
import {
  PeSignupState,
  PeSignupType,
} from '../../dashboard/dashboard/pe-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { Company } from '@openapi';
import { NGXLogger } from 'ngx-logger';
import { NavigationExtras, Router } from '@angular/router';
import { create } from '@share/utils/create';
import { mockPeConnAccessToken } from '@share/constants-mock-database';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private _interceptorType;
  private _tokenSwitched$: Subject<PeConnAccessToken>;

  constructor(
    private logger: NGXLogger,
    private router: Router,
  ) {
    this._interceptorType = InterceptorTypeEnum.EPM;
    this._tokenSwitched$ = new Subject();
  }

  setInterceptorType(type: InterceptorTypeEnum) {
    this._interceptorType = type;
  }

  /**
   * Attempts to pull authorization from an token Query param, otherwise it will use last used authorization or
   * if none currently exists, it uses a generic publicAuthorization string.
   * */
  getAuthorizationHeader(): string {
    if (this.hasTokenInStorage(PECONN_TOKEN)) {
      const auth: PeConnAccessToken = this.getPeConnAccessToken();
      return auth.token_type + ' ' + auth.access_token;
    } else {
      return '';
    }
  }

  isEyefinityPractice(): boolean {
    const company = this.getFromSessionStorage(CACHED_COMPANY, () =>
      create<Company>(),
    ) as Company;
    return company && company.efan && !company.integration?.epmIntegrated;
  }

  /**
   * All logic for initiating a reintegration has been moved from components into this method.
   * First this method will expect the location of the page that the user is reintegrating from
   * so when the integration completes pe-conn can re-navigate to the page they left from.
   * Next it will pass the signup type and state to pingfed with a redirect to the api that
   * will alert it to navigate back to pe-conn when the process is done.  Pe-conn will check
   * for the cached location saved here and complete full circle so the user is back on the
   * screen they just left.
   * @param location
   * @param signupType
   * @param signupState
   */
  reintegrate(location: string): void {
    this.addToSessionStorage(CACHED_LOCATION, location);
    this.getNativeWindow().location.assign(
      this.getEnvironment().redirectUrl + '?' + REDIRECT_FEATURE + '=true',
    );
  }

  /**
   * This method should modify the existing access token in such a way that pe-conn recognizes that a different
   * practice has been loaded in by support staff.
   */
  supportTokenOverwrite(accessToken: PeConnAccessToken) {
    this.addToSessionStorage(PECONN_TOKEN, accessToken, true);
    this._tokenSwitched$.next(accessToken);
  }

  supportEfanOverwrite(efan: string) {
    this.addToSessionStorage(EPM_EFAN, efan, true);
  }

  getSwitchedToken(): Observable<PeConnAccessToken> {
    return this._tokenSwitched$.asObservable();
  }

  /**
   * Build and return the pingfed URL necessary for pe-conn to integrate an EPM practice.  Uses stored token to
   * accomplish this.  If no token is present then their returns null;
   */
  getEpmIntegrationUrl(
    stateFeature: PeSignupType,
    stateAction: PeSignupState,
  ): string {
    const token = this.getPeConnAccessToken();
    if (!token) {
      return null;
    }
    const fullUserName = token.principal;
    const userName = fullUserName.split('_')[0];
    const devFullUserName = userName;
    const commandUrl = this.getEnvironment().commandUrl;

    let linkUrl = this.getEnvironment().epmSignOnUrl.replace(
      '{{un}}',
      userName,
    );

    if (this.getEnvironment().envName === 'dev') {
      linkUrl = linkUrl.replace('{{fun}}', devFullUserName);
    } else {
      linkUrl = linkUrl.replace('{{fun}}', fullUserName);
    }
    // State should look something like this peconn:epn:Management:9000000000
    this.logger.debug('epmIntegrationCredentals() =', linkUrl);
    linkUrl = linkUrl.replace(
      '{{state}}',
      'peconn:' + stateFeature + ':' + stateAction + ':' + token.efan,
    );
    linkUrl = linkUrl.replace('{{commandUrl}}', commandUrl);
    return linkUrl;
  }

  /**
   *
   * @param httpErrorResponse
   */
  isIntegrationError(httpErrorResponse: HttpErrorResponse): boolean {
    let isIntegrationError = false;
    if (
      httpErrorResponse &&
      httpErrorResponse.error &&
      httpErrorResponse.error.description &&
      httpErrorResponse.status
    ) {
      const errorDescription: string = httpErrorResponse.error.description;
      isIntegrationError =
        errorDescription.indexOf(INTEGRATION_ERROR) !== -1 &&
        httpErrorResponse.status === SERVER_ERROR;
    }
    return isIntegrationError;
  }

  /**
   * attempts to return an eyefinity token from local store and lets authentication service know
   * to use the eyefinity interceptor for http calls.
   * @param key
   */
  hasTokenInStorage(key: string): boolean {
    if (
      this.getEnvironment().envName === 'dev' &&
      this.getEnvironment().mockDatabase
    ) {
      return true;
    } else {
      const token = this.getFromSessionStorage(
        key,
        create<PeConnAccessToken>,
      ) as PeConnAccessToken;
      return token !== null && token.access_token?.length > 0;
    }
  }

  /**
   * Helper function to identify when someone is logged in to pe-conn as an employee rather than a practice
   */
  isLoggedInAsEmployee(): boolean {
    return this.getPeConnAccessToken()?.application === APPLICATION_NAMES.pe;
  }

  /**
   * add data to sessionStorage using a key/value pair, or in this case, name / data
   * @param key
   * @param value
   * @param overwriteExistingToken
   */
  addToSessionStorage<T>(
    key: string,
    value: T,
    overwriteExistingToken = false,
  ) {
    let existingItem: PeConnAccessToken;
    if (key === PECONN_TOKEN && this.isLoggedInAsEmployee()) {
      existingItem = this.getPeConnAccessToken();
      const noExistingCompanyId = !!(value as PeConnAccessToken).companyId;
      const efansMatch =
        existingItem.efan === (value as PeConnAccessToken).efan;
      if (overwriteExistingToken && noExistingCompanyId && efansMatch) {
        (value as PeConnAccessToken).companyId = existingItem.companyId;
      }
    }
    if (
      key === EPM_EFAN &&
      this.isLoggedInAsEmployee() &&
      !overwriteExistingToken
    ) {
      // prevent oauth from continually overwriting efan when logged in as employee
      const isFirstTimeLoad = !!sessionStorage.getItem(EPM_EFAN);
      if (isFirstTimeLoad) {
        return;
      }
    }
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  /**
   * Temporary workaround as result of EWXNG-5344.  This will allow EPN signups if an
   * admin token exists in local storage
   */
  isAdmin(): boolean {
    const isEmployee = this.isLoggedInAsEmployee();
    const isAdmin = sessionStorage.getItem(ADMIN);
    return !!isAdmin || isEmployee;
  }

  /**
   * Attempts to get the base domain based on the incoming route (page load) and will establish
   * that as the base domain for future calls.  This will allow the application to handle different
   * domains at run time rather than at build time.
   */
  getRedirectUrl(): string {
    let host = this.getNativeWindow().location.origin;
    host = host ? host : this.getEnvironment().baseDomain;
    host += this.getEnvironment().redirectUrl;
    this.logger.debug('Host resolved as : ' + host);
    return host;
  }

  /**
   * Allow us to pull a value from local storage and instantiate as any factory object we specify.
   * If no factory is given then we treat the returned value as a primative string.
   * If no such token is found, or an error is logged in the console (since we don't have a
   * means to report it to the api at this time), then we return an empty object of that type or a string.
   * ClassType cannot currently handle a collection of types.
   * When calling this, if you are specifying your return type then you will need to do something like
   * getFromSessionStorage("key", Class) as Class to inform typescript that you are getting back T
   * and not a string.
   * @param key
   * @param factory
   */
  getFromSessionStorage<T>(key: string, factory?: () => T): T | string {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return factory ? factory() : '';
    }

    try {
      const parsed = JSON.parse(raw);
      if (factory) {
        const instance = factory();
        return Object.assign(instance, parsed);
      }
      return parsed as T;
    } catch (e) {
      this.logger.error('Unable to find the key: ', key, ' in local storage.');
      return raw;
    }
  }

  /**
   * removes a token by a given name from sessionStorage
   * @param key
   */
  removeFromStorage(key: string): void {
    this.logger.debug('Local key removed: ', key);
    sessionStorage.removeItem(key);
  }

  removeAllFromStorageForLogout() {
    sessionStorage.removeItem(PECONN_TOKEN);
    sessionStorage.removeItem(EPM_EFAN);
    sessionStorage.removeItem(CLIENT_ID_QUERY_PARAM);
  }

  /**
   * Might need to change if we're working on a mobile device without access to the window object.
   **/
  getNativeWindow(): Window {
    return window;
  }

  /**
   * Used to do internal navigation without needing to reload the screen
   * @param location
   * @param extras
   */
  routeToInternalLocation(location: string, extras?: NavigationExtras) {
    this.router.navigateByUrl(location, extras);
  }

  /**
   * return a PeConnAccessToken if one exists, otherwise return an empty object
   */
  getPeConnAccessToken(): PeConnAccessToken {
    if (
      this.getEnvironment().envName === 'dev' &&
      this.getEnvironment().mockDatabase
    ) {
      return mockPeConnAccessToken();
    } else {
      const token = this.getFromSessionStorage(
        PECONN_TOKEN,
        create<PeConnAccessToken>,
      ) as PeConnAccessToken;
      const fromCache = this.getFromSessionStorage(
        CACHED_COMPANY,
        create<Company>,
      ) as Company;
      const efansMatch = token.efan === fromCache.efan;
      if (efansMatch && !token.companyId && fromCache.epmCompanyId) {
        token.companyId = fromCache.epmCompanyId;
      }
      return token;
    }
  }

  getEnvironment() {
    return environment;
  }

  handleError(err) {
    this.logger.error(
      'An error occurred when trying to load ewebextra data: ',
      err,
    );
    return of(null);
  }
}
